// Adaptive Practice Coach — pure core.
//
// A scored-mode practice driver. The piece is segmented into one or more
// overlapping CHAINS (passages); each passage runs the same cadence — drill the
// weakest bars slowly, ramp tempo as they firm up, run the built window from its
// build-end, credit mastery on clean run-throughs at target, grow the window —
// and the pool rotates among not-yet-mastered passages until the whole piece
// holds. `singleChain` collapses the pool to one forward whole-piece passage (the
// debug/test mode and the degenerate case). Bar-indexed and clock-free; the
// CoachDriver maps bars→seconds and issues commands to an Engine.
//
// Config, the small data types (BarProgress/RepResult), and the mastery gate live
// in ./coach-params; the reducer's state/input/command types live in ./coach-state
// (both leaf modules) — import those from there, not from here.

import { effectiveOpacity, feedDepth } from "./coach-escalation";
import { type BarProgress, type CoachConfig, repPassesGates } from "./coach-params";
import { type Chain, segment } from "./coach-segment";
import type {
  BarStat,
  CoachCommands,
  CoachInput,
  CoachResume,
  CoachState,
  Passage,
} from "./coach-state";

const round2 = (x: number): number => Math.round(x * 100) / 100;

/** Step tempo up toward target — coarse, then a fine step inside the band. */
export function rampUp(tempo: number, c: CoachConfig): number {
  const step = c.targetTempo - tempo <= c.tempoFineBand ? c.tempoFineStep : c.tempoStep;
  return round2(Math.min(c.targetTempo, tempo + step));
}

/** Step tempo down toward the floor (after a failed/aborted rep). */
export function rampDown(tempo: number, c: CoachConfig): number {
  return round2(Math.max(c.tempoFloor, tempo - c.tempoStep));
}

/** Density-scaled abort: mistakes as a fraction of the span's notes. Applied only to
 *  run-throughs (the continuity exam) — drills always run to completion, then the
 *  ramp tolerance decides; that's what keeps a slip-prone drill off the floor. */
export function shouldAbort(mistakes: number, notesInSpan: number, c: CoachConfig): boolean {
  if (notesInSpan <= 0) return false;
  return mistakes / notesInSpan > c.abortDensity;
}

function blankBar(): BarStat {
  return { masteryReps: 0, mastered: false, recentMistakes: 0, stuckReps: 0 };
}

/** Built window [lo, hi) of a passage. */
function built(p: Passage): [number, number] {
  return p.dir === "forward" ? [p.startBar, p.winEndBar] : [p.winStartBar, p.endBar];
}

function fullyBuilt(p: Passage): boolean {
  return p.dir === "forward" ? p.winEndBar >= p.endBar : p.winStartBar <= p.startBar;
}

function expandWindow(p: Passage, c: CoachConfig): void {
  if (p.dir === "forward") p.winEndBar = Math.min(p.winEndBar + c.expandBars, p.endBar);
  else p.winStartBar = Math.max(p.winStartBar - c.expandBars, p.startBar);
}

/** Weakest unmastered bar in [lo, hi): most mistakes, else first, else −1. */
function weakestUnmastered(bars: BarStat[], lo: number, hi: number): number {
  let pick = -1;
  let worst = -1;
  for (let b = lo; b < hi; b++) {
    const bar = bars[b];
    if (!bar || bar.mastered) continue;
    if (pick < 0) pick = b;
    if (bar.recentMistakes > worst) {
      worst = bar.recentMistakes;
      pick = b;
    }
  }
  return pick;
}

function allMastered(bars: BarStat[], lo: number, hi: number): boolean {
  for (let b = lo; b < hi; b++) {
    if (!bars[b]?.mastered) return false;
  }
  return true;
}

/** Aim the drill span at the weakest unmastered bar of the built window, ≥
 *  loopMinBars wide where the window allows, and reset tempo to the floor. */
function aimDrill(p: Passage, bars: BarStat[], c: CoachConfig): void {
  const [lo, hi] = built(p);
  const weak = weakestUnmastered(bars, lo, hi);
  const anchor = weak < 0 ? lo : weak;
  const maxStart = Math.max(lo, hi - c.loopMinBars);
  p.spanStartBar = Math.min(anchor, maxStart);
  p.spanEndBar = Math.min(p.spanStartBar + c.loopMinBars, hi);
  if (p.spanEndBar <= p.spanStartBar) p.spanEndBar = Math.min(p.spanStartBar + 1, hi);
  p.tempo = c.tempoFloor;
  p.phase = "drill";
  p.repMistakes = 0;
}

/** Re-aim the drill at a single failed bar (failRepeatBars wide), clamped to the
 *  built window. Unlike aimDrill it preserves tempo — the caller decides the drop. */
function reAimAtBar(p: Passage, bar: number, c: CoachConfig, lo: number, hi: number): void {
  const start = Math.max(lo, Math.min(bar, Math.max(lo, hi - c.failRepeatBars)));
  p.spanStartBar = start;
  p.spanEndBar = Math.min(start + c.failRepeatBars, hi);
  if (p.spanEndBar <= p.spanStartBar) p.spanEndBar = Math.min(p.spanStartBar + 1, hi);
  p.phase = "drill";
  p.repMistakes = 0;
}

function makePassage(chain: Chain, c: CoachConfig, fullBuilt: boolean, gated: boolean): Passage {
  const p: Passage = {
    startBar: chain.startBar,
    endBar: chain.endBar,
    dir: chain.dir,
    winStartBar:
      chain.dir === "forward"
        ? chain.startBar
        : Math.max(chain.startBar, chain.endBar - c.expandBars),
    winEndBar:
      chain.dir === "forward"
        ? Math.min(chain.endBar, chain.startBar + c.expandBars)
        : chain.endBar,
    phase: "drill",
    spanStartBar: chain.startBar,
    spanEndBar: chain.startBar,
    tempo: c.tempoFloor,
    repMistakes: 0,
    gated,
    done: false,
  };
  if (fullBuilt) {
    // Whole-piece passage starts fully built — run-throughs only (its bars are
    // mastered by the sub-chains); it is the end-to-end backstop.
    p.winStartBar = chain.startBar;
    p.winEndBar = chain.endBar;
    p.phase = "runThrough";
    p.spanStartBar = chain.startBar;
    p.spanEndBar = chain.endBar;
  }
  return p;
}

/** Whether a saved passage layout is well-formed and consistent with this song —
 *  guards a corrupt/stale-shaped restore (falls back to a recomputed layout). */
function validPassages(ps: readonly Passage[], barCount: number): boolean {
  if (!Array.isArray(ps) || ps.length === 0) return false;
  return ps.every(
    (p) =>
      !!p &&
      [
        p.startBar,
        p.endBar,
        p.winStartBar,
        p.winEndBar,
        p.spanStartBar,
        p.spanEndBar,
        p.tempo,
      ].every((v) => Number.isFinite(v)) &&
      (p.phase === "drill" || p.phase === "runThrough") &&
      (p.dir === "forward" || p.dir === "backward") &&
      typeof p.gated === "boolean" &&
      typeof p.done === "boolean" &&
      p.startBar >= 0 &&
      p.endBar <= barCount &&
      p.startBar < p.endBar,
  );
}

export function initCoachState(
  barCount: number,
  c: CoachConfig,
  rng: () => number,
  resume?: CoachResume,
): CoachState {
  const bars = Array.from({ length: Math.max(1, barCount) }, blankBar);
  const progress = resume?.bars;
  if (progress) {
    for (let i = 0; i < bars.length; i++) {
      const p = progress[i];
      const bar = bars[i];
      if (p && bar) {
        bar.masteryReps = Math.max(0, p.masteryReps);
        bar.mastered = p.mastered;
      }
    }
  }
  // Full resume: reuse the saved live layout verbatim (tempo ramp / phase / window
  // / active passage) so the session continues mid-climb. The store only hands back
  // `passages` when staleness decay left mastery unchanged, so a restored layout
  // always agrees with the bar mastery above; a partial rep restarts (repMistakes 0).
  if (resume?.passages && validPassages(resume.passages, bars.length)) {
    const passages = resume.passages.map((p) => ({ ...p, repMistakes: 0 }));
    const active = Math.max(0, Math.min(resume.active ?? 0, passages.length - 1));
    return { bars, passages, active, done: passages.every((p) => p.done) };
  }
  const chains = segment(bars.length, c, rng);
  const passages = chains.map((ch) => makePassage(ch, c, false, false));
  if (passages.length > 1 && c.requireFullRun) {
    passages.push(makePassage({ startBar: 0, endBar: bars.length, dir: "forward" }, c, true, true));
  }
  for (const p of passages) aimDrill(p, bars, c); // gated full-piece keeps its runThrough phase
  // re-set the gated full-piece passage to run-through (aimDrill forced drill)
  const last = passages[passages.length - 1];
  if (last?.gated) {
    last.phase = "runThrough";
    last.spanStartBar = last.startBar;
    last.spanEndBar = last.endBar;
  }
  return { bars, passages, active: 0, done: false };
}

/** Snapshot per-bar mastery for cross-session persistence (storage-agnostic). */
export function extractProgress(state: CoachState): BarProgress[] {
  return state.bars.map((b) => ({ masteryReps: b.masteryReps, mastered: b.mastered }));
}

/** Snapshot the live passage layout (tempo/phase/window/active) for resume. */
export function extractPassages(state: CoachState): { passages: Passage[]; active: number } {
  return { passages: state.passages.map((p) => ({ ...p })), active: state.active };
}

/** Per-passage mastery for the top-bar chain dots: the fraction of each chain's bars
 *  that are mastered, plus the chain's `done` flag. Pure; the view maps it to colors. */
export function chainProgress(state: CoachState): { fraction: number; done: boolean }[] {
  return state.passages.map((p) => {
    const len = Math.max(1, p.endBar - p.startBar);
    let mastered = 0;
    for (let i = p.startBar; i < p.endBar; i++) if (state.bars[i]?.mastered) mastered++;
    return { fraction: mastered / len, done: p.done };
  });
}

function clonePassage(p: Passage): Passage {
  return { ...p };
}

function clone(s: CoachState): CoachState {
  return {
    bars: s.bars.map((b) => ({ ...b })),
    passages: s.passages.map(clonePassage),
    active: s.active,
    done: s.done,
  };
}

const pct = (t: number): number => Math.round(t * 100);

function chainLabel(s: CoachState): string {
  return s.passages.length > 1 ? ` · chain ${s.active + 1}/${s.passages.length}` : "";
}

function drillCommands(s: CoachState, p: Passage): CoachCommands {
  return {
    seekToBar: p.spanStartBar,
    setWindowBars: [p.spanStartBar, p.spanEndBar],
    setTempo: p.tempo,
    status: `Drilling bars ${p.spanStartBar + 1}–${p.spanEndBar} @ ${pct(p.tempo)}%${chainLabel(s)}`,
  };
}

function runCommands(s: CoachState, p: Passage): CoachCommands {
  return {
    seekToBar: p.spanStartBar,
    setWindowBars: [p.spanStartBar, p.spanEndBar],
    setTempo: p.tempo,
    status: `Run-through · bars ${p.spanStartBar + 1}–${p.spanEndBar} @ ${pct(p.tempo)}%${chainLabel(s)}`,
  };
}

/** Index of the next not-done passage (prefer non-gated), or −1 if none. A gated
 *  passage (the whole-piece backstop) is only returned when `allowGated` — i.e.
 *  when picking the next passage after one completes, never during rotation. */
function nextActive(s: CoachState, avoid = -1, allowGated = false): number {
  let gatedFallback = -1;
  for (let k = 0; k < s.passages.length; k++) {
    const p = s.passages[k];
    if (!p || p.done || k === avoid) continue;
    if (!p.gated) return k;
    if (allowGated && gatedFallback < 0) gatedFallback = k;
  }
  return gatedFallback;
}

/** Commands that re-establish the engine for a passage's current phase. */
export function viewCommands(s: CoachState): CoachCommands {
  const p = s.passages[s.active];
  if (!p) return { status: "Done — piece mastered" };
  return p.phase === "drill" ? drillCommands(s, p) : runCommands(s, p);
}

/** The active passage's drill/run span in bars (for the driver's note count). */
export function activeSpan(s: CoachState): [number, number] {
  const p = s.passages[s.active];
  return p ? [p.spanStartBar, p.spanEndBar] : [0, 0];
}

/** Advance the coach one frame. Pure: returns a fresh state + engine commands. */
export function step(
  state: CoachState,
  input: CoachInput,
  c: CoachConfig,
): { state: CoachState; commands: CoachCommands } {
  const s = clone(state);
  if (s.done) return { state: s, commands: { status: "Done — piece mastered" } };
  const p = s.passages[s.active];
  if (!p) {
    s.done = true;
    return { state: s, commands: { status: "Done — piece mastered" } };
  }

  // A no-input pass is the player stepping away — misses here are not the player's
  // mistakes, so don't tally them (no weak-bar pollution, no ramp-down, no abort).
  if (input.newMistakes > 0 && !input.noInput) {
    const bar = s.bars[input.bar];
    if (bar) bar.recentMistakes += input.newMistakes;
    p.repMistakes += input.newMistakes;
  }

  // Mid-rep abort (run-throughs only): back off and re-drill the offending span.
  // Drills never abort — they finish, then the ramp tolerance decides. A no-input
  // pass can't abort (silence isn't sloppy playing).
  if (
    !input.reachedEnd &&
    !input.noInput &&
    p.phase === "runThrough" &&
    shouldAbort(p.repMistakes, input.notesInSpan, c)
  ) {
    const baseline = input.baselineOpacity ?? 1;
    const failBar = s.bars[input.bar];
    if (failBar) failBar.stuckReps += 1;
    const stuck = failBar?.stuckReps ?? 0;
    const [lo, hi] = built(p);
    reAimAtBar(p, input.bar, c, lo, hi); // bar-scoped quick repeat (regression fix)
    // Highway-first: drop tempo only once the feed is exhausted (stuck beyond depth K).
    if (stuck > feedDepth(baseline)) p.tempo = rampDown(p.tempo, c);
    const cmd = drillCommands(s, p);
    return {
      state: s,
      commands: {
        ...cmd,
        highwayOpacity: effectiveOpacity(baseline, stuck),
        status: `Abort — ${cmd.status}`,
      },
    };
  }

  if (!input.reachedEnd) {
    return { state: s, commands: { status: viewCommands(s).status } };
  }

  // A whole pass elapsed with no key-presses: keep tempo/phase/window exactly as they
  // were and re-establish the drill (so a resume replays from the top). No ramp, no
  // credit, no reset — the player just wasn't there. The driver pauses the loop.
  if (input.noInput) {
    p.repMistakes = 0;
    return { state: s, commands: viewCommands(s) };
  }

  // Rep completed.
  const repMistakes = p.repMistakes;
  const clean = repMistakes === 0;
  const withinTolerance = repMistakes <= c.rampTolerance;
  p.repMistakes = 0;

  if (p.phase === "drill") {
    // Speed up when within tolerance (near-clean), else ease back — never stranded.
    p.tempo = withinTolerance ? rampUp(p.tempo, c) : rampDown(p.tempo, c);
    if (withinTolerance) {
      for (let b = p.spanStartBar; b < p.spanEndBar; b++) {
        const bar = s.bars[b];
        if (bar) bar.stuckReps = 0; // recovered — the feed re-fades to baseline
      }
    }
    if (withinTolerance && p.tempo >= c.targetTempo) {
      p.phase = "runThrough";
      const [lo, hi] = built(p);
      p.spanStartBar = lo;
      p.spanEndBar = hi;
      return { state: s, commands: runCommands(s, p) };
    }
    const cmd = drillCommands(s, p);
    // Blocking feedback: name what's keeping you here so the floor isn't a silent wall.
    const status =
      repMistakes > 0
        ? `${cmd.status} · ${repMistakes} mistake${repMistakes > 1 ? "s" : ""} — play clean to speed up`
        : cmd.status;
    return { state: s, commands: { ...cmd, status } };
  }

  // Run-through completed: credit mastery only when it clears the gates at target.
  // The driver supplies repResult; without it, fall back to the zero-mistake rule.
  const passed = input.repResult
    ? repPassesGates(input.repResult, c, input.activeGates, input.timingPct)
    : clean;
  const [lo, hi] = built(p);
  if (passed && p.tempo >= c.targetTempo) {
    for (let b = lo; b < hi; b++) {
      const bar = s.bars[b];
      if (!bar) continue;
      bar.masteryReps += 1;
      bar.stuckReps = 0;
      if (bar.masteryReps >= c.masteryReps) bar.mastered = true;
    }
  } else {
    for (let b = lo; b < hi; b++) {
      const bar = s.bars[b];
      if (bar && !bar.mastered) bar.masteryReps = 0;
    }
  }

  if (allMastered(s.bars, lo, hi)) {
    if (fullyBuilt(p)) p.done = true;
    else expandWindow(p, c);
  }

  // Advance the pool: if the active passage finished, move on (the whole-piece
  // backstop is eligible here); else maybe rotate (never onto the backstop).
  if (p.done) {
    const next = nextActive(s, -1, true);
    if (next < 0) {
      s.done = true;
      return { state: s, commands: { status: "Done — piece mastered" } };
    }
    s.active = next;
  } else if (s.passages.length > 1 && input.rng() < c.pSwitchChain) {
    const other = nextActive(s, s.active);
    if (other >= 0) s.active = other;
  }

  const active = s.passages[s.active];
  if (!active) {
    s.done = true;
    return { state: s, commands: { status: "Done — piece mastered" } };
  }
  // A fully-built (whole-piece) passage stays in run-through; others re-aim a drill.
  if (active.phase === "runThrough" && fullyBuilt(active)) {
    active.spanStartBar = built(active)[0];
    active.spanEndBar = built(active)[1];
    return { state: s, commands: runCommands(s, active) };
  }
  aimDrill(active, s.bars, c);
  return { state: s, commands: drillCommands(s, active) };
}
